import { computed, nextTick, reactive, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { httpRequest } from '@/api/httpClient'
import { useAuthStore } from '@/stores/useAuthStore'
import { normalizeImageRefForServer, normalizeImageRefsForServer } from '@/utils/normalizeImageRefForServer'
import { useAmazonStore } from './workbench/useAmazonStore'

const IDB_NAME = 'zhipan_workbench'
const IDB_STORE = 'snapshots'
const IDB_KEY = 'workspace_state'

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function idbSave(data) {
  const raw = JSON.parse(JSON.stringify(data))
  const db = await openDB()
  const tx = db.transaction(IDB_STORE, 'readwrite')
  tx.objectStore(IDB_STORE).put(raw, IDB_KEY)
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

async function idbLoad() {
  const db = await openDB()
  const tx = db.transaction(IDB_STORE, 'readonly')
  const req = tx.objectStore(IDB_STORE).get(IDB_KEY)
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result || null)
    req.onerror = () => reject(req.error)
  })
}

const PROMO_SUFFIX =
  '每个镜头需明确画面呈现形式、色调风格、核心视觉焦点，并从产品展示到场景应用，要求只有一个镜头，这个镜头能够精准传递产品实际的使用场景，适配宣传片的传播节奏与受众感知需求，要求必须包含该产品。'

const FIXED_IMAGE_SETTINGS = Object.freeze({
  model: 'doubao-seedream-4-5-251128',
  size: '2K',
  watermark: true
})

const FIXED_VIDEO_SETTINGS = Object.freeze({
  model: 'doubao-seedance-1-5-pro-251215',
  ratio: '16:9',
  size: '2560x1440',
  duration: 5,
  watermark: true
})

const FIXED_ANALYSIS_SETTINGS = Object.freeze({
  model: 'doubao-seed-2-0-pro-260215'
})

const CHARGEABLE_PATHS = new Set([
  '/api/remove-bg',
  '/api/image',
  '/api/fission',
  '/api/analyze',
  '/api/copywriting',
  '/api/amazon/traffic-radar',
  '/api/video'
])

function shouldRefreshBalance(path, method, response) {
  if (!response || !response.ok) {
    return false
  }

  if (String(method || 'GET').toUpperCase() !== 'POST') {
    return false
  }

  const normalized = String(path || '').split('?')[0]
  return CHARGEABLE_PATHS.has(normalized)
}

function useCutStore({ requestRaw }) {
  const models = [
    { value: 'doubao-seedream-4-5-251128', label: 'Doubao-Seedream 4.5 2.5B' },
    { value: 'doubao-seedream-4-0', label: 'Seedream 4.0' },
    { value: 'doubao-seedream-3-2', label: 'Seedream 3.2' }
  ]

  const videoModels = [{ value: 'doubao-seedance-1-5-pro-251215', label: 'Doubao-Seedance-1.5-pro' }]

  const analysisModels = [
    { value: 'doubao-seed-1-6-flash-250828', label: 'Doubao-flash(默认)' },
    { value: 'doubao-seed-1-6-lite-251015', label: 'Doubao-Vision-Lite' },
    { value: 'doubao-seed-1-6-251015', label: 'Doubao-Seed-1.6' }
  ]

  const imageSizeOptions = [
    { value: '2K', label: '2K (2048x2048)' },
    { value: '1024*1024', label: '1024x1024' },
    { value: '768*768', label: '768x768' }
  ]

  const bgPresetOptions = [
    { value: 'white', label: '白底柔光阴影' },
    { value: 'gray', label: '浅灰干净背景' },
    { value: 'blue', label: '蓝紫渐变科技感' },
    { value: 'custom', label: '自定义颜色/描述' }
  ]

  const ratioOptions = [
    { value: '1:1', label: '1:1', size: '2048x2048' },
    { value: '4:3', label: '4:3', size: '2560x1920' },
    { value: '3:4', label: '3:4', size: '1920x2560' },
    { value: '16:9', label: '16:9', size: '2560x1440' },
    { value: '9:16', label: '9:16', size: '1440x2560' }
  ]

  const durationOptions = [
    { value: 5, label: '5 秒' },
    { value: 8, label: '8 秒' },
    { value: 10, label: '10 秒' },
    { value: 15, label: '15 秒' }
  ]

  const backgrounds = [
    { value: 'white', label: '白底', type: 'color', value2: '#ffffff' },
    { value: 'gray', label: '浅灰', type: 'color', value2: '#f5f6f7' },
    {
      value: 'gradient',
      label: '蓝紫渐变',
      type: 'gradient',
      value2: 'linear-gradient(135deg, #165DFF 0%, #9C8CFF 100%)'
    },
    { value: 'grid', label: '棋盘格', type: 'pattern', value2: 'grid' }
  ]

  const cutTab = ref('doubao')

  const local = reactive({
    image: '',
    preview: '',
    result: '',
    raw: '',
    loading: false,
    error: '',
    status: ''
  })

  const doubao = reactive({
    image: '',
    preview: '',
    result: '',
    raw: '',
    loading: false,
    error: '',
    status: '',
    product: '',
    background: '商品置于纯白背景，不要阴影，电商主图',
    bgPreset: 'white',
    size: '2048x2048',
    watermark: true,
    prompt:
      '这是一个淘宝店铺，分析商品是什么，抠图并保留完整商品，重绘背景：商品置于纯白背景，不要阴影，不要淘宝界面！删去图中人物！只要完整商品抠图！'
  })

  const localWatermark = ref(true)
  const selectedBgLocal = ref('white')
  const selectedBgDoubao = ref('white')

  const bgStyle = (bg) => {
    if (!bg) {
      return {}
    }
    if (bg.type === 'color' || bg.type === 'gradient') {
      return { background: bg.value2 }
    }
    if (bg.type === 'pattern') {
      return {
        backgroundImage:
          'linear-gradient(45deg, #eee 25%, transparent 25%),' +
          'linear-gradient(-45deg, #eee 25%, transparent 25%),' +
          'linear-gradient(45deg, transparent 75%, #eee 75%),' +
          'linear-gradient(-45deg, transparent 75%, #eee 75%)',
        backgroundSize: '12px 12px',
        backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0'
      }
    }
    return {}
  }

  const stageStyleLocal = computed(() => ({
    ...bgStyle(backgrounds.find((item) => item.value === selectedBgLocal.value)),
    minHeight: '280px'
  }))

  const stageStyleDoubao = computed(() => ({
    ...bgStyle(backgrounds.find((item) => item.value === selectedBgDoubao.value)),
    minHeight: '280px'
  }))

  const selectedCutRef = ref(null)

  const resolveLastCutFallback = () => doubao.result || local.result || ''

  const setSelectedCutRef = (payload) => {
    if (!payload || !payload.url) {
      selectedCutRef.value = null
      return
    }

    const normalizedMode = payload.mode === 'local' ? 'local' : 'doubao'
    const normalizedSlot = Number.isInteger(payload.slotIndex) ? payload.slotIndex : 0
    selectedCutRef.value = {
      mode: normalizedMode,
      slotIndex: normalizedSlot,
      url: payload.url,
      manual: payload.manual !== false
    }
  }

  const clearSelectedCutRef = () => {
    selectedCutRef.value = null
  }

  const lastCut = computed(() => {
    const selectedUrl = selectedCutRef.value?.url
    if (selectedUrl) {
      return selectedUrl
    }
    return resolveLastCutFallback()
  })

  const cutResultsByMode = reactive({ local: ['', '', ''], doubao: ['', '', ''] })

  const cutSourceImages = reactive({ local: ['', '', ''], doubao: ['', '', ''] })

  const batchCutHandler = ref(null)
  const batchCutRunning = ref(false)

  const lastCutGroup = computed(() => {
    const primary = selectedCutRef.value?.mode || cutTab.value
    const secondary = primary === 'local' ? 'doubao' : 'local'
    const urls = []
    for (const url of cutResultsByMode[primary]) {
      if (url) urls.push(url)
    }
    for (const url of cutResultsByMode[secondary]) {
      if (url && !urls.includes(url)) urls.push(url)
    }
    if (urls.length > 0) return urls
    const fallback = resolveLastCutFallback()
    return fallback ? [fallback] : []
  })

  const activeImage = computed(() => (cutTab.value === 'local' ? local.image : doubao.image))
  const activeResult = computed(() => (cutTab.value === 'local' ? local.result : doubao.result))
  const activeStatus = computed(() => (cutTab.value === 'local' ? local.status : doubao.status))
  const activeError = computed(() => (cutTab.value === 'local' ? local.error : doubao.error))
  const activeLoading = computed(() => (cutTab.value === 'local' ? local.loading : doubao.loading))
  const activeStageStyle = computed(() =>
    cutTab.value === 'local' ? stageStyleLocal.value : stageStyleDoubao.value
  )

  const compressImage = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = (event) => {
        const image = new Image()
        image.src = event.target.result
        image.onload = () => {
          let width = image.width
          let height = image.height
          const maxSize = 1024

          if (width > height) {
            if (width > maxSize) {
              height = Math.round((height * maxSize) / width)
              width = maxSize
            }
          } else if (height > maxSize) {
            width = Math.round((width * maxSize) / height)
            height = maxSize
          }

          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const context = canvas.getContext('2d')
          context.drawImage(image, 0, 0, width, height)
          resolve(canvas.toDataURL('image/jpeg', 0.7))
        }
        image.onerror = () => {
          console.warn('[compressImage] 图片加载失败')
          resolve('')
        }
      }
      reader.onerror = () => {
        console.warn('[compressImage] FileReader 失败')
        resolve('')
      }
    })

  const resetLocal = () => {
    Object.assign(local, {
      image: '',
      preview: '',
      result: '',
      raw: '',
      loading: false,
      error: '',
      status: ''
    })
    if (selectedCutRef.value?.mode === 'local') {
      clearSelectedCutRef()
    }
  }

  const resetDoubao = () => {
    Object.assign(doubao, {
      image: '',
      preview: '',
      result: '',
      raw: '',
      loading: false,
      error: '',
      status: '',
      product: doubao.product,
      background: doubao.background,
      bgPreset: doubao.bgPreset,
      size: doubao.size,
      watermark: doubao.watermark,
      prompt: doubao.prompt
    })
    if (selectedCutRef.value?.mode === 'doubao') {
      clearSelectedCutRef()
    }
  }

  const setLocalFile = async (file) => {
    if (!file) {
      return
    }
    local.preview = URL.createObjectURL(file)
    const compressed = await compressImage(file)
    local.image = compressed
    local.raw = `已压缩: ${Math.round(compressed.length / 1024)}KB`
  }

  const setDoubaoFile = async (file) => {
    if (!file) {
      return
    }
    doubao.preview = URL.createObjectURL(file)
    const compressed = await compressImage(file)
    doubao.image = compressed
    doubao.raw = `已压缩: ${Math.round(compressed.length / 1024)}KB`
  }

  const ensureImageFromPreview = async (target, fileName) => {
    if (target.image || !target.preview) {
      return
    }
    const response = await fetch(target.preview)
    if (!response.ok) {
      throw new Error('读取上传图片失败，请重新上传')
    }
    const blob = await response.blob()
    const file = new File([blob], fileName, { type: blob.type || 'image/png' })
    const compressed = await compressImage(file)
    target.image = compressed
    target.raw = `已压缩: ${Math.round(compressed.length / 1024)}KB`
  }

  const runLocal = async () => {
    try {
      await ensureImageFromPreview(local, 'local-upload.png')
    } catch (error) {
      local.error = error.message || '读取上传图片失败'
      return
    }
    if (!local.image) {
      local.error = '请先上传原始图像'
      return
    }

    local.loading = true
    local.error = ''
    local.status = ''
    local.raw = ''
    local.result = ''

    try {
      const response = await requestRaw('/api/remove-bg', {
        method: 'POST',
        body: {
          mode: 'backgroundremover',
          imageBase64: local.image
        }
      })
      if (response.status === 402) {
        throw new Error('算力不足或未登录，请充值后重试！')
      }
      const data = await response.json()
      local.raw = JSON.stringify(data, null, 2)

      if (!response.ok) {
        const detail = data?.error || data
        throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail))
      }

      local.result = data.imageBase64 || data.imageUrl || ''
      local.status = '完成'
    } catch (error) {
      local.error = error.message || '请求失败'
    } finally {
      local.loading = false
    }
  }

  const runDoubao = async () => {
    try {
      await ensureImageFromPreview(doubao, 'doubao-upload.png')
    } catch (error) {
      doubao.error = error.message || '读取上传图片失败'
      return
    }

    if (!doubao.image) {
      doubao.error = '请先上传原始图像'
      return
    }

    doubao.loading = true
    doubao.error = ''
    doubao.status = '处理中...'
    doubao.raw = ''
    doubao.result = ''

    try {
      const response = await requestRaw('/api/remove-bg', {
        method: 'POST',
        body: {
          mode: 'doubao',
          prompt: doubao.prompt,
          background: doubao.background,
          size: doubao.size,
          watermark: false,
          imageBase64: doubao.image
        }
      })
      if (response.status === 402) {
        throw new Error('算力不足或未登录，请充值后重试！')
      }
      const data = await response.json()
      doubao.raw = JSON.stringify(data, null, 2)

      if (!response.ok) {
        const detail = data?.error || data
        throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail))
      }

      doubao.result = data.imageBase64 || data.imageUrl || ''
      doubao.status = '抠图完成'
    } catch (error) {
      doubao.error = error.message || '请求失败'
    } finally {
      doubao.loading = false
    }
  }

  const startCut = async () => {
    if (batchCutHandler.value) {
      await batchCutHandler.value()
      return
    }
    const runFn = cutTab.value === 'local' ? runLocal : runDoubao
    await runFn()
  }

  const removeBgDirect = async (imageBase64, mode) => {
    const body = mode === 'local'
      ? { mode: 'backgroundremover', imageBase64 }
      : {
          mode: 'doubao',
          prompt: doubao.prompt,
          background: doubao.background,
          size: doubao.size,
          watermark: false,
          imageBase64
        }
    const response = await requestRaw('/api/remove-bg', { method: 'POST', body })
    if (response.status === 402) {
      throw new Error('算力不足或未登录，请充值后重试！')
    }
    const data = await response.json()
    if (!response.ok) {
      const detail = data?.error || data
      throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail))
    }
    return data.imageBase64 || data.imageUrl || ''
  }

  const selectBg = (value) => {
    if (cutTab.value === 'local') {
      selectedBgLocal.value = value
    } else {
      selectedBgDoubao.value = value
    }
  }

  const onWatermarkToggle = (checked) => {
    if (cutTab.value === 'doubao') {
      doubao.watermark = checked
    } else {
      localWatermark.value = checked
    }
  }

  const downloadCurrent = () => {
    const url = activeResult.value
    if (!url) {
      return
    }
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = 'cutout.png'
    anchor.click()
  }

  return {
    models,
    videoModels,
    analysisModels,
    imageSizeOptions,
    bgPresetOptions,
    ratioOptions,
    durationOptions,
    backgrounds,
    cutTab,
    local,
    doubao,
    localWatermark,
    selectedBgLocal,
    selectedBgDoubao,
    stageStyleLocal,
    stageStyleDoubao,
    selectedCutRef,
    setSelectedCutRef,
    clearSelectedCutRef,
    resolveLastCutFallback,
    lastCut,
    cutResultsByMode,
    cutSourceImages,
    lastCutGroup,
    batchCutHandler,
    batchCutRunning,
    activeImage,
    activeResult,
    activeStatus,
    activeError,
    activeLoading,
    activeStageStyle,
    bgStyle,
    compressImage,
    resetLocal,
    resetDoubao,
    setLocalFile,
    setDoubaoFile,
    runLocal,
    runDoubao,
    startCut,
    removeBgDirect,
    selectBg,
    onWatermarkToggle,
    downloadCurrent
  }
}

function createPromoItems(count = 7) {
  return Array.from({ length: count }, (_, index) => ({
    loading: false,
    image: '',
    url: '',
    raw: '',
    error: '',
    progress: 0,
    customPrompt: '',
    label: index === 0 ? '主图' : `展示 ${index}`,
    videoUrl: '',
    videoTaskId: '',
    videoStatus: '',
    videoError: '',
    videoLoading: false,
    videoPolling: false,
    videoCover: ''
  }))
}

function usePromoStore({ settings, analysisSettings, ratioOptions, lastCut, lastCutGroup, requestRaw }) {
  const promoRefTab = ref('cut')

  const promo = reactive({
    count: 7,
    prompt: '',
    ratio: '1:1',
    size: '2048x2048',
    watermark: true,
    refPreview: '',
    refData: '',
    useCutRef: true,
    log: '',
    items: createPromoItems(7),
    loadingAll: false,
    planningPrompts: false,
    activeEditIdx: 0
  })

  const promoLogs = ref([])

  const lightbox = reactive({
    show: false,
    url: ''
  })

  const promoPromptReady = computed(() =>
    promo.items.some((item) => item.customPrompt && item.customPrompt.trim())
  )

  const generateButtonText = computed(() => `生成 ${promo.count} 张宣传图`)

  const ensurePromoItems = () => {
    promo.count = 7
    const currentLength = promo.items.length

    if (currentLength < promo.count) {
      for (let index = currentLength; index < promo.count; index += 1) {
        promo.items.push({
          loading: false,
          image: '',
          url: '',
          raw: '',
          error: '',
          progress: 0,
          customPrompt: '',
          label: index === 0 ? '主图' : `展示 ${index}`,
          videoUrl: '',
          videoTaskId: '',
          videoStatus: '',
          videoError: '',
          videoLoading: false,
          videoPolling: false,
          videoCover: ''
        })
      }
    } else if (currentLength > promo.count) {
      promo.items.splice(promo.count)
    }
  }

  const addPromoLog = (message) => {
    promo.log = message
    const timestamp = new Date().toLocaleTimeString()
    promoLogs.value.push(`[${timestamp}] ${message}`)
    if (promoLogs.value.length > 50) {
      promoLogs.value.shift()
    }
  }

  const setPromoRefFile = async (file) => {
    if (!file) {
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        promo.refPreview = reader.result
        promo.refData = reader.result
      }
    }
    reader.readAsDataURL(file)
  }

  const clearPromoRef = () => {
    promo.refPreview = ''
    promo.refData = ''
  }

  const getPromoRef = () => {
    if (promo.useCutRef && lastCut.value) {
      return lastCut.value
    }
    if (promo.refData) {
      return promo.refData
    }
    return ''
  }

  const setPromoRatio = (value) => {
    const option = ratioOptions.find((item) => item.value === value)
    promo.ratio = value
    promo.size = option ? option.size : promo.size
  }

  const buildAnalyzePrompt = () => {
    const globalIntent = promo.prompt && promo.prompt.trim() ? promo.prompt.trim() : ''
    const intentBlock = globalIntent ? `用户补充意图：${globalIntent}` : '用户补充意图：无'
    return [
      '请基于参考图，为给定 labels 按顺序分别生成独立镜头提示词。',
      '要求严格一一对应 labels，输出内容不要合并镜头。',
      intentBlock,
      `统一镜头质量约束：${PROMO_SUFFIX}`
    ].join('\n')
  }

  const autoAnalyzePrompt = async () => {
    if (promo.planningPrompts || promo.loadingAll) {
      return
    }

    let refImages = []
    if (promoRefTab.value === 'cut') {
      refImages = lastCutGroup.value.filter(Boolean)
    } else {
      if (promo.refData) refImages = [promo.refData]
    }

    if (refImages.length === 0) {
      addPromoLog('请先上传或选择一张参考图（确保抠图工作台已有结果）')
      return
    }

    refImages = refImages.map((img) => {
      if (!img.startsWith('data:image') && !img.startsWith('http')) {
        return `data:image/png;base64,${img}`
      }
      return img
    })

    const refImage = refImages[0]

    try {
      promo.planningPrompts = true
      addPromoLog('正在规划 1+6 视觉方案...')

      const currentLabels = promo.items.map((item) => item.label || '')
      const response = await requestRaw('/api/analyze', {
        method: 'POST',
        body: {
          image: refImage,
          images: refImages,
          model: analysisSettings.model || '',
          prompt: buildAnalyzePrompt(),
          labels: currentLabels
        }
      })
      if (response.status === 402) {
        throw new Error('算力不足或未登录，请充值后重试！')
      }
      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || '请求失败')
      }

      let promptsArray = null
      if (Array.isArray(data)) {
        promptsArray = data
      } else if (data.result && Array.isArray(data.result)) {
        promptsArray = data.result
      } else {
        let aiText = ''
        if (data.result) {
          aiText = data.result
        } else if (data.choices && data.choices[0]) {
          const content = data.choices[0].message.content
          aiText = typeof content === 'string' ? content : JSON.stringify(content)
        }

        if (aiText) {
          try {
            const jsonString = aiText.replace(/```json/g, '').replace(/```/g, '').trim()
            if (jsonString.startsWith('[') || jsonString.startsWith('{')) {
              const parsed = JSON.parse(jsonString)
              promptsArray = Array.isArray(parsed) ? parsed : null
            }
          } catch (error) {
            // 解析失败时走降级处理
          }

          if (!promptsArray) {
            promo.items[promo.activeEditIdx].customPrompt = `${aiText}\n\n${PROMO_SUFFIX}`
            addPromoLog('未能结构化输出，已填入当前选中镜头的提示词')
            return
          }
        }
      }

      if (promptsArray && promptsArray.length > 0) {
        promo.count = 7
        ensurePromoItems()

        promptsArray.forEach((item, index) => {
          if (index < promo.items.length) {
            promo.items[index].customPrompt = item.prompt
            if (item.type) {
              promo.items[index].label = item.type
            }
          }
        })

        promo.activeEditIdx = 0
        addPromoLog('方案规划完成，点击右侧图块可分别预览或修改提示词')
      } else {
        throw new Error('未能识别有效的 AI 返回格式')
      }
    } catch (error) {
      addPromoLog(`分析失败: ${error.message}`)
    } finally {
      promo.planningPrompts = false
    }
  }

  const planAllLensPrompts = () => autoAnalyzePrompt()

  const generateOne = async (index, promptText, refImage) => {
    const item = promo.items[index]
    item.loading = true
    item.error = ''
    item.progress = 0
    item.videoUrl = ''
    item.videoTaskId = ''
    item.videoStatus = ''
    item.videoError = ''
    item.videoLoading = false
    item.videoPolling = false
    item.videoCover = ''

    const hardConstraints = '。【硬性约束：如果画面中包含模特，必须严格遵守以下条件：1. 必须为美国白人的性感模特，肤色偏白，如果衣服是V领则模特的胸丰满一点；2. 头发必须顺直自然；3.下半身必须有穿着4. 画面中只能有唯一一个人物，不要背面图，严禁出现多个人物！严禁生成多组拼图或多格分镜！照片要有实拍感,整体打光明亮不昏暗，不要像ai作图！】';
    const finalPromptText = promptText + hardConstraints;

    const duration = 20000
    const intervalTime = 100
    const startTimestamp = performance.now()
    let resolved = false
    let finished = false
    let failed = false
    let pendingUrl = ''

    const finalizeSuccess = () => {
      if (finished) {
        return
      }
      finished = true
      clearInterval(timer)
      item.progress = 100
      item.url = pendingUrl
      item.loading = false
    }

    const timer = setInterval(() => {
      const elapsed = performance.now() - startTimestamp
      const linearProgress = Math.min(99, (elapsed / duration) * 99)
      if (!resolved) {
        item.progress = linearProgress
      } else if (elapsed >= duration) {
        finalizeSuccess()
      } else {
        item.progress = linearProgress
      }
    }, intervalTime)

    const sizeToUse =
      promo.size || ratioOptions.find((option) => option.value === promo.ratio)?.size || '2K'
    
    try {
      settings.model = "doubao-seedream-5-0-260128"
      const response = await requestRaw('/api/image', {
        method: 'POST',
        body: {
          model: settings.model,
          prompt: finalPromptText,
          size: sizeToUse,
          watermark: promo.watermark,
          imageBase64: refImage
        }
      })
      if (response.status === 402) {
        throw new Error('算力不足或未登录，请充值后重试！')
      }
      const data = await response.json()
      item.raw = JSON.stringify(data, null, 2)

      if (!response.ok) {
        const detail = data?.error || data
        throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail))
      }

      const first = Array.isArray(data?.data) ? data.data[0] : data?.data?.[0]
      pendingUrl = first?.url || (first?.b64_json ? `data:image/png;base64,${first.b64_json}` : '')
      resolved = true
      const elapsed = performance.now() - startTimestamp
      if (elapsed >= duration) {
        finalizeSuccess()
      }

      addPromoLog(`完成第 ${index + 1} 张`)
    } catch (error) {
      item.error = error.message || '生成失败'
      item.progress = 0
      failed = true
      addPromoLog(`第 ${index + 1} 张失败：${item.error}`)
    } finally {
      if (failed || !resolved) {
        clearInterval(timer)
        item.loading = false
      }
    }
  }

  const generatePromoAll = async () => {
    if (promo.loadingAll || promo.planningPrompts) {
      return
    }

    ensurePromoItems()

    const refImage = getPromoRef()
    if (!refImage) {
      addPromoLog('缺少构图参考图，请先选择抠图结果或上传参考图')
      return
    }

    promo.loadingAll = true
    addPromoLog('正在为您生成全套宣传图...')

    await Promise.all(
      promo.items.map((item, index) => {
        let finalPrompt =
          item.customPrompt && item.customPrompt.trim()
            ? item.customPrompt
            : promo.prompt || 'High quality product photography'

        const ratioValue = promo.ratio || '1:1'
        if (!finalPrompt.includes('aspect ratio')) {
          finalPrompt = `${finalPrompt}, aspect ratio ${ratioValue}`
        }

        return generateOne(index, finalPrompt, refImage)
      })
    )

    promo.loadingAll = false
  }

  const clearHistory = () => {
    promo.items.splice(0, promo.items.length)
    ensurePromoItems()
    addPromoLog('已清空历史')
  }

  const downloadAll = () => {
    addPromoLog('批量下载：请右键另存或逐张点击预览后下载')
  }

  const openLightbox = (url) => {
    if (!url) {
      return
    }
    lightbox.url = url
    lightbox.show = true
  }

  const closeLightbox = () => {
    lightbox.show = false
    lightbox.url = ''
  }

  ensurePromoItems()

  return {
    promoRefTab,
    promo,
    promoLogs,
    lightbox,
    promoPromptReady,
    generateButtonText,
    ensurePromoItems,
    addPromoLog,
    setPromoRefFile,
    clearPromoRef,
    getPromoRef,
    setPromoRatio,
    planAllLensPrompts,
    autoAnalyzePrompt,
    generateOne,
    generatePromoAll,
    clearHistory,
    downloadAll,
    openLightbox,
    closeLightbox
  }
}

function useVideoStore({ videoSettings, ratioOptions, promo, addPromoLog, requestRaw }) {
  const videoGen = reactive({
    selectedIdx: -1,
    selectedIdxs: [],
    status: '',
    error: '',
    loading: false,
    url: '',
    cover: '',
    taskId: '',
    log: ''
  })

  const videoProgress = ref(0)
  const videoPlaying = ref(false)
  const videoProgressTimer = ref(null)

  const selectedVideoImage = computed(() => {
    if (videoGen.selectedIdx !== -1 && promo.items[videoGen.selectedIdx]) {
      return promo.items[videoGen.selectedIdx].url || ''
    }
    if (videoGen.selectedIdxs.length) {
      const index = videoGen.selectedIdxs[videoGen.selectedIdxs.length - 1]
      return promo.items[index]?.url || ''
    }
    return ''
  })

  const videoCover = computed(() => {
    if (videoGen.cover) {
      return videoGen.cover
    }
    if (videoGen.selectedIdx !== -1 && promo.items[videoGen.selectedIdx]) {
      return promo.items[videoGen.selectedIdx].url || ''
    }
    return ''
  })

  const setVideoRatio = (value) => {
    const option = ratioOptions.find((item) => item.value === value)
    videoSettings.ratio = value
    videoSettings.size = option ? option.size : videoSettings.size
  }

  const selectPromoForVideo = (index) => {
    const item = promo.items[index]
    if (!item || !item.url) {
      return
    }

    const exists = videoGen.selectedIdxs.includes(index)
    if (exists) {
      videoGen.selectedIdxs = videoGen.selectedIdxs.filter((value) => value !== index)
      if (videoGen.selectedIdx === index) {
        videoGen.selectedIdx = videoGen.selectedIdxs.length
          ? videoGen.selectedIdxs[videoGen.selectedIdxs.length - 1]
          : -1
      }
      addPromoLog(`已取消选择 Slot ${index + 1}`)
    } else {
      videoGen.selectedIdxs = [...videoGen.selectedIdxs, index]
      videoGen.selectedIdx = index
      addPromoLog(`已选择 Slot ${index + 1} 用于生成视频`)
    }
  }

  const downloadImage = (url) => {
    if (!url) {
      return
    }
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = 'promo.png'
    anchor.click()
  }

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

  const startVideoProgress = (durationMs = 300000) => {
    if (videoProgressTimer.value) {
      clearInterval(videoProgressTimer.value)
    }

    videoProgress.value = 0
    const startTimestamp = performance.now()

    videoProgressTimer.value = setInterval(() => {
      const elapsed = performance.now() - startTimestamp
      const linear = Math.min(99, (elapsed / durationMs) * 99)
      videoProgress.value = linear

      if (elapsed >= durationMs) {
        clearInterval(videoProgressTimer.value)
        videoProgressTimer.value = null
      }
    }, 200)
  }

  const stopVideoProgress = (finalValue = 0) => {
    if (videoProgressTimer.value) {
      clearInterval(videoProgressTimer.value)
      videoProgressTimer.value = null
    }
    videoProgress.value = finalValue
  }

  const buildVideoPrompt = () => {
    const duration = videoSettings.duration || 5
    const basePrompt = promo.prompt && promo.prompt.trim()

    if (basePrompt) {
      return `${basePrompt}，基于这张宣传图生成 ${duration} 秒产品宣传视频，确保产品全程入镜，画面流畅。尺寸比例：${videoSettings.ratio}`
    }

    return `基于参考图生成 ${duration} 秒产品宣传视频，突出产品主体、卖点与场景，保持产品全程入镜。尺寸比例：${videoSettings.ratio}`
  }

  const videoPollInterval = 3000
  const videoPollMax = 200

  const pollVideo = async (index, taskId, syncVideoGen = false) => {
    const item = promo.items[index]
    item.videoPolling = true

    if (syncVideoGen && videoGen.selectedIdx === index) {
      videoGen.loading = true
      videoGen.error = ''
      videoGen.log = '查询中...'
    }

    const pollUrl = `/api/video/${taskId}`

    try {
      for (let count = 0; count < videoPollMax; count += 1) {
        const response = await requestRaw(pollUrl)
        const data = await response.json()

        if (!response.ok) {
          const detail = data?.error || data
          throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail))
        }

        item.videoStatus = data.status || ''
        item.videoUrl = data.videoUrl || item.videoUrl
        item.videoCover = data.lastFrameUrl || data.fileUrl || item.videoCover

        if (syncVideoGen && videoGen.selectedIdx === index) {
          videoGen.status = item.videoStatus
          videoGen.url = item.videoUrl
          videoGen.cover = item.videoCover
          if (item.videoUrl) {
            stopVideoProgress(100)
          }
        }

        const status = (data.status || '').toLowerCase()

        if (status === 'succeeded') {
          if (syncVideoGen && videoGen.selectedIdx === index) {
            videoGen.log = '视频生成成功'
          }
          stopVideoProgress(100)
          return
        }

        if (status === 'failed') {
          const errorMessage = data?.error?.message || data?.error || '生成失败'
          item.videoError = errorMessage
          if (syncVideoGen && videoGen.selectedIdx === index) {
            videoGen.error = errorMessage
            videoGen.log = ''
          }
          stopVideoProgress(0)
          return
        }

        await wait(videoPollInterval)
      }

      item.videoError = '查询超时，请稍后重试'
      if (syncVideoGen && videoGen.selectedIdx === index) {
        videoGen.error = item.videoError
        videoGen.log = ''
      }
      stopVideoProgress(0)
    } catch (error) {
      item.videoError = error.message || '查询失败'
      if (syncVideoGen && videoGen.selectedIdx === index) {
        videoGen.error = item.videoError
        videoGen.log = ''
      }
      stopVideoProgress(0)
    } finally {
      item.videoPolling = false
      if (syncVideoGen && videoGen.selectedIdx === index) {
        videoGen.loading = false
      }
    }
  }

  const generateVideo = async (index, syncVideoGen = false) => {
    const item = promo.items[index]
    if (!item.url) {
      item.videoError = '请先生成并选择一张宣传图'
      if (syncVideoGen && videoGen.selectedIdx === index) {
        videoGen.error = item.videoError
        videoGen.loading = false
      }
      return
    }

    item.videoLoading = true
    item.videoError = ''
    item.videoStatus = 'pending'
    item.videoUrl = ''
    item.videoTaskId = ''

    if (syncVideoGen && videoGen.selectedIdx === index) {
      videoGen.loading = true
      videoGen.error = ''
      videoGen.status = 'pending'
      videoGen.url = ''
      videoGen.cover = ''
      videoGen.taskId = ''
      videoGen.log = '任务提交中...'
    }

    startVideoProgress(300000)

    try {
      const response = await requestRaw('/api/video', {
        method: 'POST',
        body: {
          model: videoSettings.model,
          prompt: buildVideoPrompt(),
          durationSeconds: videoSettings.duration || 5,
          size: videoSettings.ratio,
          watermark: videoSettings.watermark,
          image: item.url
        }
      })
      if (response.status === 402) {
        throw new Error('算力不足或未登录，请充值后重试！')
      }
      const data = await response.json()
      if (!response.ok) {
        const detail = data?.error || data
        throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail))
      }

      item.videoTaskId = data.taskId
      item.videoStatus = data.status || 'pending'

      if (syncVideoGen && videoGen.selectedIdx === index) {
        videoGen.taskId = data.taskId
        videoGen.status = item.videoStatus
        videoGen.log = '任务已提交，查询中...'
      }

      await pollVideo(index, data.taskId, syncVideoGen)
    } catch (error) {
      item.videoError = error.message || '生成失败'
      item.videoStatus = ''
      if (syncVideoGen && videoGen.selectedIdx === index) {
        videoGen.error = item.videoError
        videoGen.status = ''
        videoGen.log = ''
      }
      stopVideoProgress(0)
    } finally {
      item.videoLoading = false
      if (syncVideoGen && videoGen.selectedIdx === index) {
        videoGen.loading = false
      }
    }
  }

  const runVideoFromSelection = async () => {
    if (videoGen.selectedIdx === -1 && videoGen.selectedIdxs.length) {
      videoGen.selectedIdx = videoGen.selectedIdxs[videoGen.selectedIdxs.length - 1]
    }

    if (videoGen.selectedIdx === -1) {
      videoGen.error = '请先选择一张宣传图'
      return
    }

    videoPlaying.value = false
    videoGen.loading = true
    videoGen.error = ''
    videoGen.status = 'pending'
    videoGen.url = ''
    videoGen.cover = ''
    videoGen.log = '任务提交中...'
    await generateVideo(videoGen.selectedIdx, true)
  }

  const startVideoPreview = async (videoElement) => {
    if (!videoGen.url) {
      return
    }
    videoPlaying.value = true
    await nextTick()

    if (videoElement) {
      try {
        await videoElement.play()
      } catch (error) {
        // 浏览器自动播放限制时忽略错误
      }
    }
  }

  const downloadVideo = () => {
    if (!videoGen.url) {
      return
    }
    const anchor = document.createElement('a')
    anchor.href = videoGen.url
    anchor.download = 'video.mp4'
    anchor.click()
  }

  return {
    videoGen,
    videoProgress,
    videoPlaying,
    selectedVideoImage,
    videoCover,
    setVideoRatio,
    selectPromoForVideo,
    downloadImage,
    runVideoFromSelection,
    generateVideo,
    startVideoPreview,
    downloadVideo,
    stopVideoProgress
  }
}

function useFissionStore({ settings, ratioOptions, promo, videoGen, requestRaw, compressImage }) {
  const fission = reactive({
    loading: false,
    image: '',
    preview: '',
    prompt:
      '请提取图二中的商品，分析商品是什么，精准嵌入到图一的场景中。保持商品外形、材质和颜色完全不变。图一的背景环境、模特及动作保持原封不动。确保商品与场景的光影融合自然，无违和感。',
    items: []
  })

  const setFissionFile = async (file) => {
    if (!file) {
      return
    }
    fission.preview = URL.createObjectURL(file)
    try {
      fission.image = await compressImage(file)
    } catch (error) {
      console.error('裂变商品图压缩失败', error)
    }
  }

  const clearFissionSelection = () => {
    fission.loading = false
    fission.items = []
  }

  const prepareFissionFromSelection = () => {
    if (!videoGen.selectedIdxs.length) {
      return false
    }

    const selectedItems = videoGen.selectedIdxs
      .map((index) => promo.items[index])
      .filter((item) => item && item.url)

    if (!selectedItems.length) {
      alert('所选槽位尚未生成图片，请生成成功后再试。')
      return false
    }

    fission.items = selectedItems.map((item) => ({
      sourceUrl: item.url,
      resultUrl: '',
      loading: false,
      progress: 0,
      error: ''
    }))

    return true
  }

  const generateOneFission = async (index) => {
    const item = fission.items[index]
    if (!item || !item.sourceUrl || !fission.image) {
      alert('请先上传裂变商品原图并确保有参考底图')
      return
    }

    item.loading = true
    item.error = ''
    item.progress = 0
    item.resultUrl = ''

    const duration = 20000
    const startTs = performance.now()
    let resolved = false

    const timer = setInterval(() => {
      const elapsed = performance.now() - startTs
      const currentProgress = Math.min(99, (elapsed / duration) * 99)
      if (!resolved) {
        item.progress = currentProgress
      }
    }, 100)

    try {
      const normalizedBaseImage = await normalizeImageRefForServer(item.sourceUrl)
      const response = await requestRaw('/api/fission', {
        method: 'POST',
        body: {
          model: settings.model,
          prompt: fission.prompt,
          size: promo.ratio ? ratioOptions.find((option) => option.value === promo.ratio)?.size : '2K',
          watermark: settings.watermark,
          baseImage: normalizedBaseImage,
          productImage: fission.image
        }
      })
      if (response.status === 402) {
        throw new Error('算力不足或未登录，请充值后重试！')
      }
      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || '生成失败')
      }

      const first = Array.isArray(data?.data) ? data.data[0] : data?.data?.[0]
      item.resultUrl = first?.url || (first?.b64_json ? `data:image/png;base64,${first.b64_json}` : '')
      resolved = true
    } catch (error) {
      item.error = error.message || '裂变失败'
      console.error(`第 ${index + 1} 张裂变失败:`, error)
    } finally {
      clearInterval(timer)
      item.progress = 100
      item.loading = false
    }
  }

  const generateFissionAll = async () => {
    if (!fission.image) {
      alert('请先上传裂变商品原图')
      return
    }

    fission.loading = true
    await Promise.all(fission.items.map((_, index) => generateOneFission(index)))
    fission.loading = false
  }

  const downloadAllFission = async () => {
    const urls = fission.items.map((item) => item.resultUrl).filter(Boolean)
    if (urls.length === 0) {
      alert('当前没有可下载的裂变图片')
      return
    }

    if (urls.length === 1) {
      const link = document.createElement('a')
      link.href = urls[0]
      link.download = 'fission_1.png'
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      return
    }

    try {
      const normalizedUrls = await normalizeImageRefsForServer(urls)
      const response = await requestRaw('/api/download-zip', {
        method: 'POST',
        body: { urls: normalizedUrls }
      })
      if (response.status === 402) {
        throw new Error('算力不足或未登录，请充值后重试！')
      }
      if (!response.ok) {
        throw new Error('服务器打包失败')
      }

      const blob = await response.blob()
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'fission_results.zip'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    } catch (error) {
      console.error('打包下载失败:', error)
      alert(error?.message || '下载压缩包失败，请检查网络或后端服务')
    }
  }

  return {
    fission,
    setFissionFile,
    clearFissionSelection,
    prepareFissionFromSelection,
    generateOneFission,
    generateFissionAll,
    downloadAllFission
  }
}

function useSizeChartStore({ settings, promo, videoGen, requestRaw, compressImage }) {
  const sizeChart = reactive({
    loading: false,     // 用于【生成图片】的 loading 状态
    isExtracting: false, // 用于【提取数据】的 loading 状态
    image: '',
    preview: '',
    result: '',
    bgImage: '',
    tableData: null,
    size: '1024x1024'
  })

  const prepareSizeChartBgFromSelection = () => {
    if (!videoGen.selectedIdxs.length) {
      return false
    }

    const selectedItems = videoGen.selectedIdxs
      .map((index) => promo.items[index])
      .filter((item) => item && item.url)

    if (!selectedItems.length) {
      alert('所选槽位尚未生成图片，请生成成功后再试。')
      return false
    }

    sizeChart.bgImage = selectedItems[selectedItems.length - 1].url
    return true
  }

  const setSizeChartFile = async (file) => {
    if (!file) {
      return
    }

    sizeChart.preview = URL.createObjectURL(file)
    sizeChart.image = await compressImage(file)
    sizeChart.tableData = null
    sizeChart.size = '2560x1920'
  }
  const extractSizeData = async () => {
    if (!sizeChart.image) {
      throw new Error('请先上传尺码图')
    }

    sizeChart.isExtracting = true
    sizeChart.tableData = null

    try {
      // 使用 requestRaw 调用我们之前商定的新接口
      const response = await requestRaw('/api/analyze/size-chart', {
        method: 'POST',
        body: {
          model: "doubao-seed-2-0-pro-260215", // 强制使用视觉模型进行分析
          imageBase64: sizeChart.image
        }
      })
      if (response.status === 402) {
        throw new Error('算力不足或未登录，请充值后重试！')
      }
      
      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || '解析图片失败')
      }

      // 后端 Java 已经将结果序列化为了规整的 JSON（包含了 columns 和 data）
      if (data && data.columns && data.data) {
        sizeChart.tableData = data
      } else {
        throw new Error("识别数据格式异常，请重试")
      }
    } catch (error) {
      console.error('提取失败:', error)
      throw new Error(error.message || '数据提取失败')
    } finally {
      sizeChart.isExtracting = false
    }
  }
  const generateSizeChartImage = async () => {
    if (!sizeChart.tableData) {
      throw new Error('请先提取并确认尺码数据');
    }
    if (!sizeChart.image) {
      throw new Error('请先上传尺码原图');
    }

    sizeChart.loading = true;
    sizeChart.result = '';

    try {
      // 1. 获取当前用户修改后的最新表格数据，并序列化为 JSON 字符串
      const jsonData = JSON.stringify(sizeChart.tableData);

      // 2. 将 JSON 拼接入验证成功的提示词模板中
      const finalPrompt = `你是一个专业的跨境电商视觉设计师。请以图生图重绘这张中文尺码图，严格按照以下要求进行彻底的本地化（英文）改造：\n\n【核心任务：彻底英化与数据替换】\n1. 全图无中文（极度重要）：原图中的所有中文（无论是表格内的表头、内容，还是图片背景、角落里的任何说明文字）必须 100% 全部清除，重绘后的画面中绝对不能出现任何一个汉字！\n2. 精准数据替换：请使用以下最新英文数据（inch/lb）重绘表格，必须严格按照此 JSON 结构和数值渲染，不可遗漏或拼错任何一个字母与数字：\n${jsonData}\n3. 视觉排版：保持原图的排版风格，确保行列数据精准对齐，文字清晰锐利，不能比原图表格添加行列。\n\n【附加任务：防退货温馨提示】\n4. 底部嵌入提示：请在生成的尺码图最底部正中、显眼的位置，清晰地印上这句英文防退货提示：\n"Please carefully check the size chart. If you are unsure, please choose one size larger."`;

      // 3. 强制使用 Seedream 4.5 画图模型
      settings.model = "doubao-seedream-4-5-251128";

      const response = await requestRaw('/api/image', {
        method: 'POST',
        body: {
          model: settings.model,
          prompt: finalPrompt,
          size: '2560x1920',
          watermark: false,
          imageBase64: sizeChart.image
        }
      });
      
      if (response.status === 402) {
        throw new Error('算力不足或未登录，请充值后重试！');
      }
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || '生成图片失败');
      }

      const first = Array.isArray(data?.data) ? data.data[0] : data?.data?.[0];
      let finalImageUrl = first?.url || (first?.b64_json ? `data:image/png;base64,${first.b64_json}` : '');

      // 4. 如果有防扫描底图，走合成逻辑（保留你原有的逻辑）
      if (sizeChart.bgImage && finalImageUrl) {
        const [safeBgImage, safeFgImage] = await Promise.all([
          normalizeImageRefForServer(sizeChart.bgImage),
          normalizeImageRefForServer(finalImageUrl)
        ]);
        const combineRes = await requestRaw('/api/combine-size-chart', {
          method: 'POST',
          body: { bgImage: safeBgImage, fgImage: safeFgImage }
        });
        const combineData = await combineRes.json();
        if (!combineRes.ok) throw new Error(combineData.error || '合成底图失败');
        finalImageUrl = combineData.result;
      }

      sizeChart.result = finalImageUrl;
    } catch (error) {
      console.error('图片生成失败:', error);
      throw new Error(error.message || '图片生成失败');
    } finally {
      sizeChart.loading = false;
    }
  }

  return {
    sizeChart,
    setSizeChartFile,
    generateSizeChartImage,
    prepareSizeChartBgFromSelection,
    extractSizeData
  }
}

export const useWorkbenchStore = defineStore('workbench', () => {
  const authStore = useAuthStore()
  const settings = reactive({ ...FIXED_IMAGE_SETTINGS })
  const videoSettings = reactive({ ...FIXED_VIDEO_SETTINGS })
  const analysisSettings = reactive({ ...FIXED_ANALYSIS_SETTINGS })

  let _saveStateFn = null
  let _restoring = false

  const requestRaw = async (path, options = {}) => {
    const response = await httpRequest(path, { ...options, responseType: 'raw', throwOnHttpError: false })

    if (shouldRefreshBalance(path, options?.method, response)) {
      authStore.refreshProfileSilently()
    }

    return response
  }

  const cutStore = useCutStore({ requestRaw })
  const { compressImage, ...cutExports } = cutStore

  cutExports.compressImage = compressImage

  const promoStore = usePromoStore({
    settings,
    analysisSettings,
    ratioOptions: cutStore.ratioOptions,
    lastCut: cutStore.lastCut,
    lastCutGroup: cutStore.lastCutGroup,
    requestRaw
  })

  const videoStore = useVideoStore({
    videoSettings,
    ratioOptions: cutStore.ratioOptions,
    promo: promoStore.promo,
    addPromoLog: promoStore.addPromoLog,
    requestRaw
  })

  const fissionStore = useFissionStore({
    settings,
    ratioOptions: cutStore.ratioOptions,
    promo: promoStore.promo,
    videoGen: videoStore.videoGen,
    requestRaw,
    compressImage
  })

  const amazonStore = useAmazonStore({
    analysisSettings,
    lastCut: cutStore.lastCut,
    lastCutGroup: cutStore.lastCutGroup,
    requestRaw,
    compressImage
  })

  const sizeChartStore = useSizeChartStore({
    settings,
    promo: promoStore.promo,
    videoGen: videoStore.videoGen,
    requestRaw,
    compressImage
  })

  const collectState = () => ({
    cut: {
      doubaoPrompt: cutStore.doubao.prompt,
      doubaoBackground: cutStore.doubao.background,
      doubaoBgPreset: cutStore.doubao.bgPreset,
      doubaoSize: cutStore.doubao.size,
      cutResultsLocal: cutStore.cutResultsByMode.local,
      cutResultsDoubao: cutStore.cutResultsByMode.doubao,
      cutSourceLocal: cutStore.cutSourceImages.local,
      cutSourceDoubao: cutStore.cutSourceImages.doubao
    },
    promo: {
      items: promoStore.promo.items.map((item) => ({
        url: item.url,
        customPrompt: item.customPrompt,
        label: item.label,
        videoUrl: item.videoUrl,
        videoCover: item.videoCover
      })),
      prompt: promoStore.promo.prompt,
      ratio: promoStore.promo.ratio,
      useCutRef: promoStore.promo.useCutRef
    },
    fission: {
      items: fissionStore.fission.items.map((item) => ({
        sourceUrl: item.sourceUrl,
        resultUrl: item.resultUrl
      })),
      image: fissionStore.fission.image,
      prompt: fissionStore.fission.prompt
    },
    amazon: {
      result: amazonStore.amazon.result,
      keywordVolumeData: amazonStore.amazon.keywordVolumeData
    },
    sizeChart: {
      image: sizeChartStore.sizeChart.image,
      result: sizeChartStore.sizeChart.result,
      tableData: sizeChartStore.sizeChart.tableData,
      bgImage: sizeChartStore.sizeChart.bgImage
    }
  })

  let saveTimer = null
  const doSave = async () => {
    try {
      await idbSave(collectState())
    } catch (e) {
      console.warn('保存工作台状态失败:', e)
    }
  }
  const saveState = () => {
    if (saveTimer) {
      clearTimeout(saveTimer)
    }
    saveTimer = setTimeout(doSave, 1000)
  }
  const saveNow = () => {
    if (saveTimer) {
      clearTimeout(saveTimer)
      saveTimer = null
    }
    return doSave()
  }

  const restoreState = async () => {
    try {
      const data = await idbLoad()
      if (!data || typeof data !== 'object' || !Object.keys(data).length) {
        return
      }
      const state = typeof data === 'string' ? JSON.parse(data) : data
      _restoring = true

      if (state.cut) {
        if (state.cut.doubaoResult) {
          cutStore.doubao.result = state.cut.doubaoResult
          cutStore.doubao.status = '已恢复'
        }
        if (state.cut.doubaoPrompt) cutStore.doubao.prompt = state.cut.doubaoPrompt
        if (state.cut.doubaoBackground) cutStore.doubao.background = state.cut.doubaoBackground
        if (state.cut.doubaoBgPreset) cutStore.doubao.bgPreset = state.cut.doubaoBgPreset
        if (state.cut.doubaoSize) cutStore.doubao.size = state.cut.doubaoSize

        if (state.cut.cutResultsDoubao) {
          cutStore.cutResultsByMode.doubao = state.cut.cutResultsDoubao
          const first = state.cut.cutResultsDoubao.find(Boolean)
          if (first) {
            cutStore.doubao.result = first
            cutStore.doubao.status = '已恢复'
          }
        }
        if (state.cut.cutResultsLocal) {
          cutStore.cutResultsByMode.local = state.cut.cutResultsLocal
          const first = state.cut.cutResultsLocal.find(Boolean)
          if (first) {
            cutStore.local.result = first
            cutStore.local.status = '已恢复'
          }
        }
        if (state.cut.cutSourceDoubao) {
          cutStore.cutSourceImages.doubao = state.cut.cutSourceDoubao
          console.log('[restore] cutSourceDoubao slots:', state.cut.cutSourceDoubao.filter(Boolean).length)
          const first = state.cut.cutSourceDoubao.find(Boolean)
          if (first) {
            cutStore.doubao.image = first
            cutStore.doubao.preview = first
          }
        }
        if (state.cut.cutSourceLocal) {
          cutStore.cutSourceImages.local = state.cut.cutSourceLocal
          console.log('[restore] cutSourceLocal slots:', state.cut.cutSourceLocal.filter(Boolean).length)
          const first = state.cut.cutSourceLocal.find(Boolean)
          if (first) {
            cutStore.local.image = first
            cutStore.local.preview = first
          }
        }

        // Legacy fallback for older snapshots
        if (!state.cut.cutResultsDoubao && state.cut.doubaoResult) {
          cutStore.doubao.result = state.cut.doubaoResult
          cutStore.doubao.status = '已恢复'
        }
        if (!state.cut.cutSourceDoubao && state.cut.doubaoImage) {
          cutStore.doubao.image = state.cut.doubaoImage
          cutStore.doubao.preview = state.cut.doubaoImage
        }
        if (!state.cut.cutResultsLocal && state.cut.localResult) {
          cutStore.local.result = state.cut.localResult
          cutStore.local.status = '已恢复'
        }
        if (!state.cut.cutSourceLocal && state.cut.localImage) {
          cutStore.local.image = state.cut.localImage
          cutStore.local.preview = state.cut.localImage
        }
      }

      if (state.promo?.items) {
        state.promo.items.forEach((saved, index) => {
          if (index < promoStore.promo.items.length) {
            const item = promoStore.promo.items[index]
            if (saved.url) item.url = saved.url
            if (saved.customPrompt) item.customPrompt = saved.customPrompt
            if (saved.label) item.label = saved.label
            if (saved.videoUrl) item.videoUrl = saved.videoUrl
            if (saved.videoCover) item.videoCover = saved.videoCover
          }
        })
        if (state.promo.prompt) promoStore.promo.prompt = state.promo.prompt
        if (state.promo.ratio) promoStore.promo.ratio = state.promo.ratio
        if (state.promo.useCutRef !== undefined) promoStore.promo.useCutRef = state.promo.useCutRef

        const firstVideoItem = state.promo.items.find((i) => i.videoUrl)
        if (firstVideoItem) {
          const vIndex = state.promo.items.indexOf(firstVideoItem)
          videoStore.videoGen.selectedIdx = vIndex
          videoStore.videoGen.url = firstVideoItem.videoUrl
          videoStore.videoGen.cover = firstVideoItem.videoCover || ''
        }
      }

      if (state.fission) {
        if (state.fission.image) {
          fissionStore.fission.image = state.fission.image
          fissionStore.fission.preview = state.fission.image
        }
        if (state.fission.prompt) fissionStore.fission.prompt = state.fission.prompt
        if (state.fission.items?.length) {
          fissionStore.fission.items = state.fission.items
        }
      }

      if (state.amazon) {
        if (state.amazon.result) amazonStore.amazon.result = state.amazon.result
        if (state.amazon.keywordVolumeData?.length) {
          amazonStore.amazon.keywordVolumeData = state.amazon.keywordVolumeData
        }
      }

      if (state.sizeChart) {
        if (state.sizeChart.image) {
          sizeChartStore.sizeChart.image = state.sizeChart.image
          sizeChartStore.sizeChart.preview = state.sizeChart.image
        }
        if (state.sizeChart.result) {
          sizeChartStore.sizeChart.result = state.sizeChart.result
        }
        if (state.sizeChart.tableData) {
          sizeChartStore.sizeChart.tableData = state.sizeChart.tableData
        }
        if (state.sizeChart.bgImage) {
          sizeChartStore.sizeChart.bgImage = state.sizeChart.bgImage
        }
      }
    } catch (e) {
      console.warn('恢复工作台状态失败:', e)
    } finally {
      _restoring = false
    }
  }

  restoreState()

  _saveStateFn = saveState

  const origGenerateFissionAll = fissionStore.generateFissionAll
  fissionStore.generateFissionAll = async () => {
    await origGenerateFissionAll()
    saveState()
  }

  const origGeneratePromoAll = promoStore.generatePromoAll
  promoStore.generatePromoAll = async () => {
    await origGeneratePromoAll()
    saveState()
  }

  const origAutoAnalyzePrompt = promoStore.autoAnalyzePrompt
  promoStore.autoAnalyzePrompt = async () => {
    await origAutoAnalyzePrompt()
    saveState()
  }

  const origGenerateAmazonCopy = amazonStore.generateAmazonCopy
  amazonStore.generateAmazonCopy = async () => {
    await origGenerateAmazonCopy()
    saveState()
  }

  const origAnalyzeTrafficAll = amazonStore.analyzeTrafficAll
  amazonStore.analyzeTrafficAll = async () => {
    await origAnalyzeTrafficAll()
    saveState()
  }

  const origExtractSizeData = sizeChartStore.extractSizeData
  sizeChartStore.extractSizeData = async () => {
    await origExtractSizeData()
    saveState()
  }

  const origGenerateSizeChartImage = sizeChartStore.generateSizeChartImage
  sizeChartStore.generateSizeChartImage = async () => {
    await origGenerateSizeChartImage()
    saveState()
  }

  const autoSaveWatch = (getter) => {
    watch(getter, () => {
      if (!_restoring && _saveStateFn) _saveStateFn()
    })
  }

  autoSaveWatch(() => JSON.stringify(cutStore.cutResultsByMode))
  autoSaveWatch(() => JSON.stringify(cutStore.cutSourceImages))
  autoSaveWatch(() => fissionStore.fission.image)
  autoSaveWatch(() => JSON.stringify(amazonStore.amazon.result))
  autoSaveWatch(() => amazonStore.amazon.keywordVolumeData.length)
  autoSaveWatch(() => sizeChartStore.sizeChart.image)
  autoSaveWatch(() => sizeChartStore.sizeChart.result)

  watch(() => promoStore.promo.items, () => {
    if (!_restoring && _saveStateFn) _saveStateFn()
  }, { deep: true })

  watch(() => fissionStore.fission.items, () => {
    if (!_restoring && _saveStateFn) _saveStateFn()
  }, { deep: true })

  return {
    PROMO_SUFFIX,
    settings,
    videoSettings,
    analysisSettings,
    ...cutExports,
    ...promoStore,
    ...videoStore,
    ...fissionStore,
    ...amazonStore,
    ...sizeChartStore,
    saveState,
    saveNow,
    restoreState
  }
})
